export async function encryptAES(data: string, key: string): Promise<string> {
  const encoder = new TextEncoder()
  const dataBuffer = encoder.encode(data)
  const keyBuffer = await crypto.subtle.digest('SHA-256', encoder.encode(key))

  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    keyBuffer,
    { name: 'AES-GCM' },
    false,
    ['encrypt']
  )

  const iv = crypto.getRandomValues(new Uint8Array(12))
  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: iv },
    cryptoKey,
    dataBuffer
  )

  const result = new Uint8Array(iv.length + encrypted.byteLength)
  result.set(iv, 0)
  result.set(new Uint8Array(encrypted), iv.length)

  return btoa(String.fromCharCode(...result))
}

export async function decryptAES(encryptedData: string, key: string): Promise<string> {
  const encoder = new TextEncoder()
  const encryptedBuffer = Uint8Array.from(atob(encryptedData), (c) => c.charCodeAt(0))
  const keyBuffer = await crypto.subtle.digest('SHA-256', encoder.encode(key))

  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    keyBuffer,
    { name: 'AES-GCM' },
    false,
    ['decrypt']
  )

  const iv = encryptedBuffer.slice(0, 12)
  const data = encryptedBuffer.slice(12)

  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: iv },
    cryptoKey,
    data
  )

  return new TextDecoder().decode(decrypted)
}

export function base64Encode(str: string): string {
  return btoa(encodeURIComponent(str))
}

export function base64Decode(str: string): string {
  return decodeURIComponent(atob(str))
}

export function encodeURI(str: string): string {
  return encodeURIComponent(str)
}

export function decodeURI(str: string): string {
  return decodeURIComponent(str)
}

export function xorEncrypt(str: string, key: string): string {
  let result = ''
  for (let i = 0; i < str.length; i++) {
    result += String.fromCharCode(str.charCodeAt(i) ^ key.charCodeAt(i % key.length))
  }
  return result
}

export function xorDecrypt(str: string, key: string): string {
  return xorEncrypt(str, key)
}

/**
 * 自定义加密函数
 * @description 使用指定版本的加密算法对字符串进行加密，支持中文
 * @doc true
 * @param data 要加密的字符串，支持中文
 * @param version 加密版本，目前只支持v1
 * @returns {string} 加密后的字符串
 * @example
 * const encrypted = PRL.djjenc('Hello World', 'v1');
 * console.log(encrypted); // 输出加密后的字符串
 * 
 * // 支持中文
 * const encryptedChinese = PRL.djjenc('你好世界', 'v1');
 * console.log(encryptedChinese); // 输出加密后的中文
 */
export function djjenc(data: string, version: string='v1'): string {
  // 验证版本，目前只支持v1
  if (version !== 'v1') {
    throw new Error(`Unsupported encryption version: ${version}. Only v1 is supported.`)
  }
  
  // 取出version的第二个字符
  const versionChar = version[1];
  
  // 生成两个随机字符，用于版本号前两位
  const randomChar1 = String.fromCharCode(Math.floor(Math.random() * 26) + 65); // 生成A-Z的随机字符
  const randomChar2 = String.fromCharCode(Math.floor(Math.random() * 26) + 65); // 生成A-Z的随机字符
  
  // 构建新的版本号格式：两个随机字符 + 版本字符
  const newVersion = randomChar1 + randomChar2 + versionChar;
  
  // 将字符串转为16进制
  let hex = ''
  for (let i = 0; i < data.length; i++) {
    const charCode = data.charCodeAt(i)
    // 确保每个字符都用4位16进制表示，支持中文
    hex += charCode.toString(16).padStart(4, '0')
  }
  
  // 对每个16进制字符进行操作
  let encryptedHex = ''
  for (let i = 0; i < hex.length; i += 2) {
    let firstChar = hex[i]
    let secondChar = hex[i + 1]
    
    // 第一个字符-1
    let firstValue = parseInt(firstChar, 16)
    firstValue = firstValue === 0 ? 15 : firstValue - 1
    firstChar = firstValue.toString(16)
    
    // 第二个字符-2
    let secondValue = parseInt(secondChar, 16)
    secondValue = secondValue < 2 ? secondValue + 14 : secondValue - 2
    secondChar = secondValue.toString(16)
    
    encryptedHex += firstChar + secondChar
  }
  
  // 在前面增加版本前缀
  return newVersion + encryptedHex
}

/**
 * 自定义解密函数
 * @description 使用指定版本的解密算法对字符串进行解密，支持中文
 * @doc true
 * @param data 要解密的字符串
 * @returns {string} 解密后的字符串，支持中文
 * @example
 * const decrypted = PRL.djjdec('v1...'); // 传入加密后的字符串
 * console.log(decrypted); // 输出解密后的字符串
 * 
 * // 解密中文
 * const decryptedChinese = PRL.djjdec(encryptedChinese);
 * console.log(decryptedChinese); // 输出解密后的中文
 */
export function djjdec(data: string): string {
  // 忽略前两个字符，取出第三个字符，与'v'组合作为版本号
  const versionChar = data.substring(2, 3)
  const version = 'v' + versionChar
  const encryptedHex = data.substring(3)
  
  if (version === 'v1') {
    // 使用v1的逆向算法还原数据
    let hex = ''
    for (let i = 0; i < encryptedHex.length; i += 2) {
      let firstChar = encryptedHex[i]
      let secondChar = encryptedHex[i + 1]
      
      // 第一个字符+1
      let firstValue = parseInt(firstChar, 16)
      firstValue = firstValue === 15 ? 0 : firstValue + 1
      firstChar = firstValue.toString(16).padStart(1, '0')
      
      // 第二个字符+2
      let secondValue = parseInt(secondChar, 16)
      secondValue = (secondValue + 2) % 16
      secondChar = secondValue.toString(16).padStart(1, '0')
      
      hex += firstChar + secondChar
    }
    
    // 将16进制转回字符串
    let result = ''
    for (let i = 0; i < hex.length; i += 4) {
      const charCode = parseInt(hex.substring(i, i + 4), 16)
      result += String.fromCharCode(charCode)
    }
    
    return result
  } else {
    // 不支持的版本
    throw new Error(`Unsupported encryption version: ${version}`)
  }
}
